import React, { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { HiNewspaper, HiCog, HiMenu, HiX } from "react-icons/hi"
import { ReactComponent as Icon } from "../assets/gettips_icon_purple.svg"
import { ReactComponent as Logo } from "../assets/gettips_logo_purple.svg"
import defaultBackground from "../assets/city_background.jpg"
import { twClassNames } from "../helpers/classNames"
import { getLocaleValue } from "../helpers/getLocaleValue"
import { parseImage } from "../helpers/parseImage"
import { Destination, Activity } from "../constants/types"

const NavigationTypeEnum = {
  handbook: "handbook",
  activity: "activity",
  account: "account"
} as const

type NavigationType = keyof typeof NavigationTypeEnum

type SidebarProps = {
  selectedProject?: Destination | Activity
  type: NavigationType
}

type NavigationItem = {
  key: string
  title: string
  icon?: React.ReactNode
  href?: string
}

const handbookNavigation: NavigationItem[] = [
  {
    key: "spacer",
    title: "sidebar.handbook.handbook"
  },
  {
    key: "guides",
    title: "sidebar.handbook.guides",
    icon: <HiNewspaper className="tw-text-xl" />,
    href: "/handbook/guides"
  },
  {
    key: "settings",
    title: "sidebar.handbook.settings",
    icon: <HiCog className="tw-text-xl" />,
    href: "/handbook/settings"
  },
  {
    key: "spacer",
    title: "sidebar.handbook.details"
  },
  {
    key: "examples",
    title: "sidebar.handbook.examples",
    href: "/handbook/examples"
  }
]

const activitiesNavigation: NavigationItem[] = [
  {
    key: "spacer",
    title: "sidebar.activities.activities"
  },
  {
    key: "campaigns",
    title: "sidebar.activities.campaigns",
    icon: <HiNewspaper className="tw-text-xl" />,
    href: "/activities/campaigns"
  },
  {
    key: "profile",
    title: "sidebar.activities.profile",
    icon: <HiCog className="tw-text-xl" />,
    href: "/activities/profile"
  }
]

const accountNavigation: NavigationItem[] = [
  {
    key: "spacer",
    title: "sidebar.account.personal"
  },
  {
    key: "personalSettings",
    title: "sidebar.account.personalSettings",
    icon: <HiCog className="tw-text-xl" />,
    href: "/account"
  },
  {
    key: "spacer",
    title: "sidebar.account.team"
  },
  {
    key: "teamSettings",
    title: "sidebar.account.teamSettings",
    icon: <HiCog className="tw-text-xl" />,
    href: "/account/team"
  }
]

const ProjectsButton = ({ data, lang }: { data: Destination | Activity; lang: string }) => {
  const title = getLocaleValue(data, "name", lang)
  const image = data.image ? parseImage(data.image) : defaultBackground

  return (
    <div className="tw-relative">
      <Link
        to="/handbooks"
        className="tw-flex tw-items-center tw-w-full tw-p-4 tw-min-h-[85px] tw-bg-light tw-border-b tw-border-t
          tw-border-gray hover:tw-no-underline hover:cursor-pointer hover:tw-bg-gray hover:tw-text-dark">
        {image && (
          <img
            src={image}
            alt=""
            className="tw-object-cover tw-h-[44px] tw-w-11 tw-rounded-xl tw-justify-self-start tw-mr-2"
          />
        )}
        <div className="tw-flex tw-flex-col tw-leading-tight tw-text-left tw-grow">
          <span className="tw-line-clamp--2 tw-font-bold">{title}</span>
        </div>
      </Link>
    </div>
  )
}

const Sidebar: React.FC<SidebarProps> = ({ selectedProject, type }) => {
  const [visible, setVisibility] = useState(false)
  const location = useLocation()
  const { t, i18n } = useTranslation()

  const isActive = (item: NavigationItem) => {
    if (!item.href) return false
    // For exact match on root paths like /account
    if (location.pathname === item.href) return true
    // For nested paths, check if current path end with item href
    if (location.pathname.endsWith(item.href)) return true
    return false
  }

  const navigation = (type: NavigationType) => {
    switch (type) {
      case NavigationTypeEnum.handbook:
        return handbookNavigation
      case NavigationTypeEnum.activity:
        return activitiesNavigation
      case NavigationTypeEnum.account:
        return accountNavigation
      default:
        return []
    }
  }

  return (
    <>
      <div className={"tw-px-0 sidebar-navigation " + (visible ? "show" : "hide")}>
        <div className="tw-flex tw-flex-col tw-h-full tw-bg-white tw-shadow">
          <Logo title="Get Tips logo" className="tw-my-10 tw-mx-auto tw-w-[7rem]" />
          {selectedProject && <ProjectsButton data={selectedProject} lang={i18n.resolvedLanguage ?? "en"} />}
          <div className="tw-flex tw-flex-col tw-space-y-2 tw-mt-4 tw-grow">
            {navigation(type).map((item, index) => {
              if (item.key === "spacer") {
                return (
                  <div className="tw-w-full tw-pt-4 tw-px-5 tw-border-b tw-border-gray" key={index}>
                    <p className="tw-text-dark-50 tw-uppercase tw-text-sm tw-mb-2">{t(item.title)}</p>
                  </div>
                )
              }
              return (
                <Link
                  key={index}
                  to={item.href || "#"}
                  className={twClassNames(
                    isActive(item)
                      ? "tw-bg-primary tw-text-white tw-font-semibold hover:tw-text-white"
                      : "hover:tw-text-dark hover:tw-bg-gray",
                    `tw-mx-2.5 tw-h-[50px] tw-px-4 tw-flex tw-items-center tw-rounded-xl tw-space-x-2.5
                    hover:tw-no-underline`
                  )}
                  aria-current={isActive(item) ? "page" : undefined}>
                  {item.icon} <span>{t(item.title)}</span>
                </Link>
              )
            })}
          </div>
          <div className="tw-flex tw-items-center tw-py-4 tw-px-5 tw-space-x-5 tw-border-t tw-border-gray">
            <Icon title="Get Tips icon" className="tw-object-scale-down tw-h-[24px] tw-w-[24px]" />
            <div className="tw-flex tw-flex-col tw-text-sm">
              <span>Get Tips © {new Date().getFullYear()}</span>
              <span className="tw-text-dark-50 tw-capitalize">
                {process.env.NODE_ENV} {process.env.REACT_APP_VERSION} ({process.env.REACT_APP_BUILD})
              </span>
            </div>
          </div>
        </div>
      </div>
      <button
        onClick={() => setVisibility(!visible)}
        className={twClassNames(
          "tw-text-white tw-bg-dark tw-rounded tw-px-3 tw-py-2 sidebar-toggle",
          visible ? "open" : "closed"
        )}>
        {visible ? <HiX /> : <HiMenu />}
      </button>
    </>
  )
}

export default Sidebar
